import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ShieldCheck, Loader2, RotateCcw, Save, UserPlus, History, Cpu, Activity, Send, PowerOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Display metadata per timer (order + friendly title + anchor + icon).
const TIMERS = [
  { key: 'assign',  title: 'Assign mechanic', anchor: 'from API-1', icon: UserPlus },
  { key: 'history', title: 'Service history', anchor: 'from API-1', icon: History },
  { key: 'ai',      title: 'AI analysis',     anchor: 'from API-2', icon: Cpu },
  { key: 'obd',     title: 'OBD received',    anchor: 'from API-2', icon: Activity },
  { key: 'api4',    title: 'Send API-4',      anchor: 'from API-2', icon: Send },
];

function authHeaders() {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// A boolean flag counts as enabled unless it is explicitly false.
const isOn = (v) => v !== false;

export default function MfcwlSlaTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({});        // key -> minutes (num/str) | *_enabled -> bool
  const [loaded, setLoaded] = useState({});         // pristine copy for Reset
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null });

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/b2b/mfcwl/admin/sla-config`, { headers: { ...authHeaders() } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const cfg = {};
      Object.entries(data.config || {}).forEach(([k, v]) => { cfg[k] = v; });
      setValues(cfg);
      setLoaded(cfg);
      setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
    } catch (e) {
      toast.error('Failed to load SLA settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const dirty = useMemo(
    () => Object.keys(values).some((k) => String(values[k]) !== String(loaded[k])),
    [values, loaded],
  );

  const setField = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const clientErrors = useMemo(() => {
    const errs = [];
    TIMERS.forEach((t) => {
      const warnOn = isOn(values[`${t.key}_warn_enabled`]);
      const hardOn = isOn(values[`${t.key}_hard_enabled`]);
      const w = Number(values[`${t.key}_warn_min`]);
      const h = Number(values[`${t.key}_hard_min`]);
      if (warnOn && !(w > 0)) errs.push(`${t.title}: warn must be positive`);
      if (hardOn && !(h > 0)) errs.push(`${t.title}: deadline must be positive`);
      if (warnOn && hardOn && w >= h) errs.push(`${t.title}: warn (${w}m) must be less than deadline (${h}m)`);
    });
    return errs;
  }, [values]);

  const save = async () => {
    if (clientErrors.length) { toast.error(clientErrors[0]); return; }
    setSaving(true);
    try {
      const payload = {};
      Object.keys(values).forEach((k) => {
        payload[k] = k.endsWith('_enabled') ? isOn(values[k]) : Number(values[k]);
      });
      const res = await fetch(`${API}/api/b2b/mfcwl/admin/sla-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const errs = data?.detail?.errors;
        throw new Error(Array.isArray(errs) ? errs.join(' · ') : (data?.detail || `HTTP ${res.status}`));
      }
      const cfg = {};
      Object.entries(data.config || {}).forEach(([k, v]) => { cfg[k] = v; });
      setValues(cfg); setLoaded(cfg);
      toast.success('SLA thresholds saved — applies on the next monitor sweep');
      load();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 p-8" data-testid="mfcwl-sla-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading SLA settings…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mfcwl-sla-tab">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 rounded-lg bg-indigo-50 p-2"><ShieldCheck className="h-5 w-5 text-indigo-600" /></div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">MFCWL SOW SLA thresholds</h3>
          <p className="text-sm text-gray-500">
            Business-editable deadlines in <span className="font-medium">minutes</span>. Each timer has a
            <span className="text-amber-600 font-medium"> warn</span> and a
            <span className="text-rose-600 font-medium"> deadline</span> (warn must be lower). Use the toggle to
            switch a threshold off — a disabled threshold never fires; turning both off disables the whole timer.
            Changes apply on the next monitor sweep (within ~1 minute).
          </p>
          {meta.updated_at && (
            <p className="text-xs text-gray-400 mt-1">
              Last changed {new Date(meta.updated_at).toLocaleString('en-IN')}{meta.updated_by ? ` by ${meta.updated_by}` : ''}
            </p>
          )}
        </div>
      </div>

      <div className="grid gap-3">
        {TIMERS.map((t) => {
          const Icon = t.icon;
          const wk = `${t.key}_warn_min`, hk = `${t.key}_hard_min`;
          const wek = `${t.key}_warn_enabled`, hek = `${t.key}_hard_enabled`;
          const warnOn = isOn(values[wek]);
          const hardOn = isOn(values[hek]);
          const timerOff = !warnOn && !hardOn;
          const bad = warnOn && hardOn &&
            (Number(values[wk]) >= Number(values[hk]) || !(Number(values[wk]) > 0) || !(Number(values[hk]) > 0));
          return (
            <div key={t.key}
                 className={`flex flex-wrap items-center gap-4 rounded-xl border p-4 transition-colors ${timerOff ? 'border-gray-200 bg-gray-50 opacity-70' : 'border-gray-200 bg-white'}`}
                 data-testid={`sla-row-${t.key}`}>
              <div className="flex items-center gap-2 min-w-[190px]">
                <Icon className="h-4 w-4 text-indigo-500" />
                <div>
                  <div className="text-sm font-medium text-gray-800">{t.title}</div>
                  <div className="text-[11px] text-gray-400">{t.anchor}</div>
                </div>
                {timerOff && (
                  <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-[10px] font-medium text-gray-600"
                        data-testid={`sla-timer-off-${t.key}`}>
                    <PowerOff className="h-3 w-3" /> Off
                  </span>
                )}
              </div>

              {/* Warn threshold + enable toggle */}
              <div className="flex items-center gap-2">
                <Switch checked={warnOn} onCheckedChange={(v) => setField(wek, v)}
                        aria-label={`${t.title} warn enabled`} data-testid={`sla-toggle-${wek}`} />
                <Label className={`text-xs w-12 ${warnOn ? 'text-amber-600' : 'text-gray-400'}`}>Warn</Label>
                <Input type="number" min="1" value={values[wk] ?? ''} disabled={!warnOn}
                       onChange={(e) => setField(wk, e.target.value)}
                       className="w-24 h-9 disabled:opacity-50" data-testid={`sla-input-${wk}`} />
                <span className="text-xs text-gray-400">min</span>
              </div>

              {/* Deadline threshold + enable toggle */}
              <div className="flex items-center gap-2">
                <Switch checked={hardOn} onCheckedChange={(v) => setField(hek, v)}
                        aria-label={`${t.title} deadline enabled`} data-testid={`sla-toggle-${hek}`} />
                <Label className={`text-xs w-16 ${hardOn ? 'text-rose-600' : 'text-gray-400'}`}>Deadline</Label>
                <Input type="number" min="1" value={values[hk] ?? ''} disabled={!hardOn}
                       onChange={(e) => setField(hk, e.target.value)}
                       className="w-24 h-9 disabled:opacity-50" data-testid={`sla-input-${hk}`} />
                <span className="text-xs text-gray-400">min</span>
              </div>

              {bad && <span className="text-[11px] text-rose-600">warn must be &lt; deadline</span>}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={save} disabled={saving || !dirty || clientErrors.length > 0}
                data-testid="sla-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save thresholds
        </Button>
        <Button variant="outline" onClick={() => setValues(loaded)} disabled={saving || !dirty}
                data-testid="sla-reset-btn">
          <RotateCcw className="h-4 w-4 mr-2" /> Reset
        </Button>
        {dirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
      </div>
    </div>
  );
}
